/* eslint-disable react-hooks/exhaustive-deps */
import * as Dialog from '@radix-ui/react-dialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useOrganizationBilling,
  useOrganizationLimits,
} from '~/accountContext';
import ComposerPopover, {
  actions as composerPopoverActions,
} from '~/composer-popover';
import { selectShowComposer } from '~/composer-popover/selectors';
import { parseIdeaToComposerPost } from '~/content/parsers';
import { markExtension } from '~/content/state/ideasSlice';
import { Idea, NewIdea } from '~/content/types';
import { selectProfiles } from '~/profile-sidebar/selectors';
import { history, useAppDispatch, useAppSelector } from '~/store';
import { useFullStory } from '~/thirdParty/hooks/useFullStory';
import { useUploader } from '~/uploads/hooks/useUploader';
import { useDropTarget } from '~/uploads/hooks/useDropTarget';
import {
  selectPendingCount,
  selectPendingProgress,
} from '~/uploads/state/selectors';
import { selectUserIdAndEmail } from '~/user/selectors';
import { addPermanentNotification } from '~/notifications-provider/notificationsSlice';
import { IDEAS_UPLOADER_ID } from '../config';
import { DragAndDrop } from '../DragAndDrop';
import { Editor } from '../Editor';
import { Header } from '../Header';
import { useIdeasCount } from '../hooks/useIdeasCount';
import { useIdeasEditor } from '../hooks/useIdeasEditor';
import { List } from '../List';
import { IdeasLimitReachedModal } from '../modals/IdeasLimitReachedModal';
import { PostSuccessModal } from '../modals/PostSuccessModal';
import { Layout, Wrapper } from './styles';

const IDEAS_LIMIT_REACHED_ERROR = 'ideaCreateLimitReachedError';

const Content = (): JSX.Element => {
  const ideaListRef = useRef(undefined);
  const location = useLocation();
  const showComposer = useAppSelector(selectShowComposer);
  const user = useAppSelector(selectUserIdAndEmail);
  const [postSuccessMessage, setPostSuccessMessage] = useState('');
  const [convertedIdea, setConvertedIdea] = useState<Idea | NewIdea | null>(
    null
  );
  const noOp = useCallback(() => undefined, []);
  const [showIdeasLimitReachedModal, setShowIdeasLimitReachedModal] =
    useState<boolean>(false);

  const { ideas: ideasLimit } = useOrganizationLimits();
  const { canStartTrial, subscription } = useOrganizationBilling();
  const { ideasCount } = useIdeasCount();
  const profiles = useAppSelector(selectProfiles);
  const dispatch = useAppDispatch();

  const ideasLimitReachedModalType = () => {
    const planId = subscription?.plan?.id;

    if (planId && planId !== 'free') {
      return 'paid';
    }

    return canStartTrial ? 'trial' : 'upgrade';
  };
  const {
    isOpen,
    currentIdea,
    showErrorMessage,
    errorCode,
    onOpenChange,
    openEditor,
    saveIdea,
    getCurrentContent,
    closeEditor,
    plateEditor,
  } = useIdeasEditor();

  useFullStory();

  const showError = (message) =>
    dispatch(addPermanentNotification(`⚠️ ${message}`));

  const { uploader } = useUploader({
    id: IDEAS_UPLOADER_ID,
    onUnmount: 'destroy',
    eventHandlers: {
      'file-validation-failed': (_, error) => showError(error.message),
      'upload-failed': (_, error) => showError(error.message),
      'file-fetch-failed': () =>
        showError(
          'We are really sorry but we cannot get the file at this moment. Please, try it later.'
        ),
    },
  });

  const pendingCount = useAppSelector((state) =>
    selectPendingCount(state, IDEAS_UPLOADER_ID)
  );
  const pendingProgress = useAppSelector((state) =>
    selectPendingProgress(state, IDEAS_UPLOADER_ID)
  );
  const isUploading = pendingCount > 0;

  const { isDragging, handleDragLeave, handleDragOver, handleDrop } =
    useDropTarget({ uploader });

  useEffect(() => {
    if (ideasCount > ideasLimit) {
      setShowIdeasLimitReachedModal(true);
    }
  }, [ideasCount]);

  useEffect(() => {
    if (!isOpen && isUploading) {
      const timeout = setTimeout(openEditor, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, isUploading]);

  const handleDismissPostSuccessModal = () => {
    setPostSuccessMessage('');
    setConvertedIdea(null);
  };

  const onDismissComposer = () => {
    dispatch(composerPopoverActions.hideComposer());
    setPostSuccessMessage('Post discarded!');
  };

  const onSaveComposerPost = ({ message }: { message: string }) => {
    dispatch(composerPopoverActions.hideComposer());
    setPostSuccessMessage(`${message}  ✅`);
  };

  const onCreatePost = () => {
    const idea = getCurrentContent();
    if (idea) {
      dispatch(
        composerPopoverActions.handleCreatePostClick({
          selectedProfileId: null,
          selectedProfileIds: [],
          cta: null,
          shouldResetComposerData: false,
          post: profiles.length
            ? parseIdeaToComposerPost(idea as Idea, user)
            : null,
        })
      );
      /** Set the currentIdea to a variable to avoid
         losing the currentIdea on close editor * */
      setConvertedIdea(idea);
      closeEditor();
    }
  };

  const isIdeasLimitReached = ideasCount >= ideasLimit;

  useEffect(() => {
    if (isIdeasLimitReached && !isOpen && isUploading) {
      const timeout = setTimeout(openEditor, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, isUploading, isIdeasLimitReached]);

  // Open ideas limit reached modal when ideas limit reached before saving idea
  useEffect(() => {
    if (errorCode && errorCode === IDEAS_LIMIT_REACHED_ERROR) {
      setShowIdeasLimitReachedModal(true);
      closeEditor();
    }
  }, [errorCode]);

  const onOpenEditor = useCallback(
    (open: boolean) => {
      const isAtLimit = ideasCount >= ideasLimit;
      if (isAtLimit && open) {
        setShowIdeasLimitReachedModal(true);
      } else {
        onOpenChange(open);
      }
    },
    [ideasCount, ideasLimit]
  );

  // Open Composer if route is /content/new
  // It will populate the editor with the content of the text
  // URL parameter
  // TODO: how to add media via URL params
  useEffect(() => {
    const shouldCreateIdea = location.pathname === '/content/new' && !isOpen;
    const text = new URLSearchParams(location.search).get('text') || '';
    const mediaUrls = new URLSearchParams(location.search).getAll('media');
    const isFromExtension =
      new URLSearchParams(location.search).get('source') || false;

    if (!shouldCreateIdea) return;

    if (isFromExtension) {
      dispatch(markExtension({ fromExtension: true }));
    }

    uploader.addFilesFromUrls(
      mediaUrls.map((url) => ({
        remoteUrl: url,
        useSafeImage: true,
      }))
    );

    const idea = {
      content: {
        text,
      },
    };

    openEditor(idea);
    history.push('/content');
  }, [isOpen, location, openEditor]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleDropIfPossible = useCallback(
    (ev) => {
      if (!isIdeasLimitReached) {
        handleDrop(ev);
      } else {
        ev.preventDefault();
        handleDragLeave(ev);
        setShowIdeasLimitReachedModal(true);
      }
    },
    [isIdeasLimitReached]
  );

  return (
    <Layout
      data-testid="content_section"
      onDragOver={isOpen ? noOp : handleDragOver}
      onDragLeave={isOpen ? noOp : handleDragLeave}
      onDrop={isOpen ? noOp : handleDropIfPossible}
    >
      <Wrapper>
        <Dialog.Root
          open={isOpen}
          modal={false}
          onOpenChange={(open) => onOpenEditor(open)}
        >
          <Dialog.Portal container={ideaListRef.current}>
            <Editor
              onSaveIdea={saveIdea}
              showErrorMessage={showErrorMessage}
              containerRef={ideaListRef.current}
              onCreatePost={onCreatePost}
              onOpenChange={onOpenChange}
              currentIdea={currentIdea}
              plateEditor={plateEditor}
            />
          </Dialog.Portal>
          <Header />
          <List onCreateNewIdea={openEditor} onEditIdea={openEditor} />
        </Dialog.Root>
      </Wrapper>
      {!isOpen && (
        <DragAndDrop
          isDragging={isDragging}
          isUploading={isUploading}
          uploadsProgress={pendingProgress}
          uploadsCount={pendingCount}
        />
      )}
      {showIdeasLimitReachedModal && (
        <IdeasLimitReachedModal
          onClose={setShowIdeasLimitReachedModal}
          type={ideasLimitReachedModalType()}
        />
      )}
      {showComposer && (
        <ComposerPopover
          onSave={onSaveComposerPost}
          type="composerPopover"
          onConfirmDismissComposer={onDismissComposer}
        />
      )}
      {postSuccessMessage && convertedIdea && (
        <PostSuccessModal
          idea={convertedIdea}
          composerSuccessMessage={postSuccessMessage}
          onDiscardModal={handleDismissPostSuccessModal}
        />
      )}
    </Layout>
  );
};

Content.propTypes = {};

Content.defaultProps = {};

export default Content;
